// The agent's WebRTC softphone (SIP.js). Signalling is SIP over WSS straight to FreeSWITCH; media is
// DTLS-SRTP with ICE between the browser and FreeSWITCH. The Node backend never carries media: it
// only hands the browser its SIP credentials, WSS URL and ICE servers (GET /api/agent/me).
//
// Supports REGISTER, incoming INVITE (answer / reject), outgoing INVITE, HOLD / RESUME (re-INVITE),
// DTMF (RFC 4733 via RTCDTMFSender, SIP INFO fallback), blind TRANSFER (REFER), BYE, mute.
//
// SIP debug: a per-browser switch (softphone checkbox, or window.__ccPhone.sipDebug(true|false) in the
// console), kept in localStorage 'cc-sip-debug'. On: SIP.js logs at debug level and prints every SIP
// message sent and received over the WebSocket (traceSip). It applies at once, no reload or rebuild.
//
// Fresh registration (2026-10-09-b): unregisterAll() sends REGISTER with "Contact: *" and Expires 0,
// which removes every binding of this extension (other tabs, other devices, stale ones), and
// reregister() then sends a new REGISTER. hooks/agent.ts freshRegistration() runs both around the
// server-side flush on portal login and logout; disconnect() also clears every binding.

import { create } from 'zustand';
import {
  UserAgent, Registerer, RegistererState, Inviter, Invitation, Session, SessionState, Web, Core, type InvitationAcceptOptions,
} from 'sip.js';
import { t } from '@/i18n';

export type RegState = 'disconnected' | 'connecting' | 'registered' | 'unregistered' | 'failed';
export type CallState = 'idle' | 'incoming' | 'outgoing' | 'active' | 'ended';

export interface SipConfig {
  username: string; password: string; domain: string; wss_url: string; ice_servers: RTCIceServer[];
}

interface PhoneState {
  reg: RegState;
  regError: string | null;
  call: CallState;
  held: boolean;
  muted: boolean;
  remote: string | null;          // caller / callee identity
  direction: 'in' | 'out' | null;
  since: number | null;           // when the current call was answered (ms)
  autoAnswer: boolean;            // answer every incoming call at once
  autoAnswerUntil: number;        // answer the next incoming call until this time (calls the agent asked for)
  sipDebug: boolean;              // SIP.js debug logging + SIP message trace in the browser console
  log: string[];
  connect: (cfg: SipConfig) => Promise<void>;
  disconnect: () => Promise<void>;
  unregisterAll: () => Promise<void>;
  reregister: () => Promise<boolean>;
  answer: () => Promise<void>;
  reject: () => Promise<void>;
  dial: (target: string) => Promise<void>;
  hangup: () => Promise<void>;
  hold: () => Promise<void>;
  resume: () => Promise<void>;
  toggleMute: () => void;
  dtmf: (digit: string) => Promise<void>;
  transfer: (target: string) => Promise<void>;
  setAutoAnswer: (v: boolean) => void;
  setSipDebug: (v: boolean) => void;
  expectCall: (ms?: number) => void;
}

let ua: UserAgent | null = null;
let registerer: Registerer | null = null;
let session: Session | null = null;
let cfgDomain = '';
let audioEl: HTMLAudioElement | null = null;
let retries = 0;
let retryTimer: ReturnType<typeof setTimeout> | null = null;

function scheduleReconnect(): void {
  if (!ua || retryTimer) return;
  const delay = Math.min(30000, 1000 * 2 ** Math.min(retries++, 5));
  retryTimer = setTimeout(() => {
    retryTimer = null;
    ua?.reconnect().catch(() => scheduleReconnect());
  }, delay);
}

function remoteAudio(): HTMLAudioElement {
  if (!audioEl) {
    audioEl = document.createElement('audio');
    audioEl.autoplay = true;
    audioEl.id = 'cc-remote-audio';
    document.body.appendChild(audioEl);
  }
  return audioEl;
}

function sdh(s: Session | null): Web.SessionDescriptionHandler | null {
  return (s?.sessionDescriptionHandler as Web.SessionDescriptionHandler | undefined) ?? null;
}

// mute / hold: stop sending our microphone without renegotiating
function enableSenders(h: Web.SessionDescriptionHandler | null, enable: boolean): void {
  h?.peerConnection?.getSenders().forEach((sn) => { if (sn.track) sn.track.enabled = enable; });
}

function target(t: string) {
  const v = t.trim();
  const uri = UserAgent.makeURI(v.startsWith('sip:') ? v : `sip:${v}@${cfgDomain}`);
  if (!uri) throw new Error('BAD_TARGET');
  return uri;
}

function readSipDebug(): boolean {
  try { return localStorage.getItem('cc-sip-debug') === '1'; } catch { return false; }
}

// switch logging on the running user agent (both are read on every log call / message)
function applySipDebug(on: boolean): void {
  if (!ua) return;
  const internals = ua as unknown as { loggerFactory?: { level: Core.Levels } };
  if (internals.loggerFactory) internals.loggerFactory.level = on ? Core.Levels.debug : Core.Levels.warn;
  const tr = ua.transport as unknown as { configuration?: { traceSip?: boolean } };
  if (tr.configuration) tr.configuration.traceSip = on;
}

const MEDIA = { constraints: { audio: true, video: false } };

// wait for the answer to an (un)REGISTER; SIP.js only resolves when the request is sent
function answered(send: (d: Core.OutgoingRequestDelegate) => Promise<unknown>, ms = 8000): Promise<string | null> {
  return new Promise((resolve) => {
    const t = setTimeout(() => resolve('TIMEOUT'), ms);
    const done = (v: string | null) => { clearTimeout(t); resolve(v); };
    send({ onAccept: () => done(null), onReject: (r) => done(`${r.message.statusCode} ${r.message.reasonPhrase}`) })
      .catch((e: Error) => done(e.message));
  });
}

export const usePhone = create<PhoneState>((set, get) => {
  const note = (m: string) => set((s) => ({ log: [...s.log.slice(-49), `${new Date().toLocaleTimeString()} ${m}`] }));

  function track(s: Session, direction: 'in' | 'out', remote: string) {
    session = s;
    set({ call: direction === 'in' ? 'incoming' : 'outgoing', direction, remote, held: false, muted: false, since: null });
    s.stateChange.addListener((st) => {
      note(`call ${st}`);
      if (st === SessionState.Established) {
        const h = sdh(s);
        if (h) {
          const el = remoteAudio();
          el.srcObject = h.remoteMediaStream;
          el.play().catch(() => {});
        }
        set({ call: 'active', since: Date.now() });
      } else if (st === SessionState.Terminated) {
        if (session === s) session = null;
        const el = audioEl;
        if (el) el.srcObject = null;
        set({ call: 'idle', held: false, muted: false, remote: null, direction: null, since: null });
      }
    });
  }

  return {
    reg: 'disconnected', regError: null, call: 'idle', held: false, muted: false, remote: null, direction: null, since: null,
    autoAnswer: (() => { try { return localStorage.getItem('cc-auto-answer') === '1'; } catch { return false; } })(),
    autoAnswerUntil: 0, log: [], sipDebug: readSipDebug(),

    async connect(cfg) {
      if (ua) await get().disconnect();
      cfgDomain = cfg.domain;
      set({ reg: 'connecting', regError: null });
      const uri = UserAgent.makeURI(`sip:${cfg.username}@${cfg.domain}`);
      if (!uri) { set({ reg: 'failed', regError: 'BAD_SIP_URI' }); return; }
      let firstConnect = true;
      ua = new UserAgent({
        uri,
        authorizationUsername: cfg.username,
        authorizationPassword: cfg.password,
        displayName: cfg.username,
        transportOptions: { server: cfg.wss_url, traceSip: get().sipDebug },
        sessionDescriptionHandlerFactoryOptions: { peerConnectionConfiguration: { iceServers: cfg.ice_servers ?? [] }, iceGatheringTimeout: 2000 },
        logLevel: get().sipDebug ? 'debug' : 'warn',
        delegate: {
          onInvite: (inv: Invitation) => {
            if (session) { inv.reject({ statusCode: 486 }).catch(() => {}); return; }      // one call at a time
            const who = inv.remoteIdentity.displayName || inv.remoteIdentity.uri.user || t('unknown');
            note(t('incoming call from {{who}}', { who }));
            track(inv, 'in', who);
            const st = get();
            if (st.autoAnswer || Date.now() < st.autoAnswerUntil) { set({ autoAnswerUntil: 0 }); get().answer(); }
          },
          // lost the WebSocket: try again with backoff while this softphone is still wanted
          onDisconnect: (err?: Error) => {
            note(`transport down${err ? `: ${err.message}` : ''}`);
            set({ reg: 'disconnected' });
            if (err) scheduleReconnect();
          },
          // re-register only after a reconnect: the first REGISTER is sent by connect() below, and a second
          // one while it is still pending is refused by SIP.js (RFC 3261 "prior one still in progress")
          onConnect: () => { note('transport up'); retries = 0; if (!firstConnect) registerer?.register().catch(() => {}); },
        },
      });
      registerer = new Registerer(ua, { expires: 120 });
      registerer.stateChange.addListener((st) => {
        note(`registration ${st}`);
        set({ reg: st === RegistererState.Registered ? 'registered' : st === RegistererState.Unregistered ? 'unregistered' : get().reg });
      });
      try {
        await ua.start();
        firstConnect = false;
        await registerer.register({
          requestDelegate: { onReject: (r) => { set({ reg: 'failed', regError: `${r.message.statusCode} ${r.message.reasonPhrase}` }); } },
        });
      } catch (e) {
        set({ reg: 'failed', regError: (e as Error).message });
      }
    },

    async disconnect() {
      try { if (session) await get().hangup(); } catch { /* already gone */ }
      await get().unregisterAll();
      const old = ua;
      ua = null; registerer = null;
      if (retryTimer) { clearTimeout(retryTimer); retryTimer = null; }
      try { await old?.stop(); } catch { /* ignore */ }
      set({ reg: 'disconnected' });
    },

    // remove every registration of this extension (Contact: *), not only this browser's
    async unregisterAll() {
      const r = registerer;
      if (!r || !ua?.isConnected()) return;
      const why = await answered((requestDelegate) => r.unregister({ all: true, requestDelegate }));
      note(why ? t('clearing registrations failed: {{why}}', { why }) : t('all registrations cleared'));
    },

    // a new REGISTER; true once FreeSWITCH accepted it
    async reregister() {
      const r = registerer;
      if (!r || !ua?.isConnected()) return false;
      set({ reg: 'connecting', regError: null });
      const why = await answered((requestDelegate) => r.register({ requestDelegate }));
      if (why) set({ reg: 'failed', regError: why });
      note(why ? t('registration failed: {{why}}', { why }) : t('registered again'));
      return !why;
    },

    async answer() {
      const s = session;
      if (!(s instanceof Invitation) || s.state !== SessionState.Initial) return;
      await s.accept({ sessionDescriptionHandlerOptions: MEDIA } as InvitationAcceptOptions);
    },

    async reject() {
      const s = session;
      if (s instanceof Invitation && s.state === SessionState.Initial) await s.reject({ statusCode: 486 });
    },

    async dial(t) {
      if (!ua || session) return;
      const inv = new Inviter(ua, target(t), { sessionDescriptionHandlerOptions: MEDIA });
      track(inv, 'out', t);
      await inv.invite();
    },

    async hangup() {
      const s = session;
      if (!s) return;
      if (s.state === SessionState.Established) await s.bye();
      else if (s instanceof Inviter && (s.state === SessionState.Initial || s.state === SessionState.Establishing)) await s.cancel();
      else if (s instanceof Invitation && s.state === SessionState.Initial) await s.reject({ statusCode: 486 });
    },

    async hold() {
      const s = session;
      if (!s || s.state !== SessionState.Established || get().held) return;
      s.sessionDescriptionHandlerModifiersReInvite = [Web.holdModifier];
      await s.invite({ requestDelegate: { onAccept: () => { enableSenders(sdh(s), false); set({ held: true }); note(t('on hold')); } } });
    },

    async resume() {
      const s = session;
      if (!s || s.state !== SessionState.Established || !get().held) return;
      s.sessionDescriptionHandlerModifiersReInvite = [];
      await s.invite({ requestDelegate: { onAccept: () => { enableSenders(sdh(s), !get().muted); set({ held: false }); note(t('resumed')); } } });
    },

    toggleMute() {
      const muted = !get().muted;
      enableSenders(sdh(session), !muted && !get().held);
      set({ muted });
    },

    async dtmf(digit) {
      const s = session;
      if (!s || s.state !== SessionState.Established || !/^[0-9*#A-D]$/.test(digit)) return;
      const sent = sdh(s)?.sendDtmf(digit, { duration: 120, interToneGap: 70 });
      if (!sent) {
        await s.info({ requestOptions: { body: { contentDisposition: 'render', contentType: 'application/dtmf-relay', content: `Signal=${digit}\r\nDuration=120` } } });
      }
      note(`DTMF ${digit}`);
    },

    async transfer(to) {
      const s = session;
      if (!s || s.state !== SessionState.Established) return;
      await s.refer(target(to), { requestDelegate: { onAccept: () => note(t('transferred to {{target}}', { target: to })), onReject: () => note(t('transfer to {{target}} refused', { target: to })) } });
    },

    setAutoAnswer(v) {
      try { localStorage.setItem('cc-auto-answer', v ? '1' : '0'); } catch { /* ignore */ }
      set({ autoAnswer: v });
    },

    setSipDebug(v) {
      try { localStorage.setItem('cc-sip-debug', v ? '1' : '0'); } catch { /* ignore */ }
      applySipDebug(v);
      set({ sipDebug: v });
      console.info(`[softphone] SIP debug ${v ? 'on' : 'off'}`);
    },

    expectCall(ms = 30000) { set({ autoAnswerUntil: Date.now() + ms }); },
  };
});

// Diagnostics for support and the browser test: media and DTLS state of the current call.
(window as unknown as { __ccPhone?: unknown }).__ccPhone = {
  // SIP debug from the console: __ccPhone.sipDebug(true) / (false); no argument returns the current value
  sipDebug(on?: boolean) {
    if (typeof on === 'boolean') usePhone.getState().setSipDebug(on);
    return usePhone.getState().sipDebug;
  },
  async stats() {
    const pc = sdh(session)?.peerConnection;
    if (!pc) return null;
    const out: Record<string, unknown> = { connectionState: pc.connectionState, iceConnectionState: pc.iceConnectionState };
    (await pc.getStats()).forEach((r) => {
      if (r.type === 'inbound-rtp' && r.kind === 'audio') { out.packetsReceived = r.packetsReceived; out.bytesReceived = r.bytesReceived; }
      if (r.type === 'outbound-rtp' && r.kind === 'audio') { out.packetsSent = r.packetsSent; }
      if (r.type === 'transport') { out.dtlsState = r.dtlsState; out.srtpCipher = r.srtpCipher; }
      if (r.type === 'codec' && r.mimeType?.startsWith('audio/') && !r.mimeType.includes('telephone')) out.codec = r.mimeType;
    });
    return out;
  },
};
